import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "hiddenField"]

  connect() {
    this.editorTarget.addEventListener("input", () => this._syncHidden())
    this.editorTarget.addEventListener("paste", (e) => this._handlePaste(e))
    this.editorTarget.addEventListener("keydown", (e) => this._handleKeydown(e))

    // Sync hidden field before form submit
    const form = this.element.closest("form") || this.element
    if (form.tagName === "FORM") {
      form.addEventListener("submit", () => this._syncHidden())
    }

    this._syncHidden()
  }

  // Toolbar actions
  bold(e) {
    e.preventDefault()
    document.execCommand("bold", false)
    this.editorTarget.focus()
  }

  italic(e) {
    e.preventDefault()
    document.execCommand("italic", false)
    this.editorTarget.focus()
  }

  underline(e) {
    e.preventDefault()
    document.execCommand("underline", false)
    this.editorTarget.focus()
  }

  insertLink(e) {
    e.preventDefault()
    const url = prompt("Enter URL:")
    if (url) {
      const selection = window.getSelection()
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        document.execCommand("createLink", false, url)
        const links = this.editorTarget.querySelectorAll("a")
        links.forEach(a => {
          if (a.href === url || a.getAttribute("href") === url) {
            a.style.color = "#C4A484"
          }
        })
      } else {
        const a = document.createElement("a")
        a.href = url
        a.style.color = "#C4A484"
        a.textContent = url
        this._insertAtCursor(a)
      }
    }
    this.editorTarget.focus()
    this._syncHidden()
  }

  bulletList(e) {
    e.preventDefault()
    document.execCommand("insertUnorderedList", false)
    this.editorTarget.focus()
  }

  clearFormatting(e) {
    e.preventDefault()
    document.execCommand("removeFormat", false)
    this.editorTarget.focus()
    this._syncHidden()
  }

  // Merge variable insertion
  insertVariable(event) {
    event.preventDefault()
    const variable = event.currentTarget.dataset.variable
    this.editorTarget.focus()
    document.execCommand("insertHTML", false, variable)
    this._syncHidden()
  }

  // Private helpers
  _handlePaste(e) {
    e.preventDefault()
    const html = e.clipboardData.getData("text/html")
    const text = e.clipboardData.getData("text/plain")

    if (html) {
      const cleaned = this._cleanHtml(html)
      document.execCommand("insertHTML", false, cleaned)
    } else {
      document.execCommand("insertText", false, text)
    }
    this._syncHidden()
  }

  _cleanHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const body = doc.body

    body.querySelectorAll("*").forEach(el => {
      const attrs = Array.from(el.attributes)
      attrs.forEach(attr => {
        if (attr.name !== "href" && attr.name !== "style") {
          el.removeAttribute(attr.name)
        }
      })
      if (el.style) {
        const allowed = ["font-weight", "font-style", "text-decoration", "color"]
        const style = el.style
        const kept = {}
        allowed.forEach(prop => {
          if (style[prop]) kept[prop] = style[prop]
        })
        el.removeAttribute("style")
        Object.entries(kept).forEach(([k, v]) => { el.style[k] = v })
      }
    })

    body.querySelectorAll("div").forEach(div => {
      const p = doc.createElement("p")
      p.innerHTML = div.innerHTML
      div.replaceWith(p)
    })

    return body.innerHTML
  }

  _handleKeydown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault()
      document.execCommand("bold", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "i") {
      e.preventDefault()
      document.execCommand("italic", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "u") {
      e.preventDefault()
      document.execCommand("underline", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault()
      this.insertLink(e)
    }
    setTimeout(() => this._syncHidden(), 0)
  }

  _insertAtCursor(node) {
    const sel = window.getSelection()
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(node)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      this.editorTarget.appendChild(node)
    }
    this._syncHidden()
  }

  _syncHidden() {
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = this.editorTarget.innerHTML
    }
  }
}
